import {useAuth, useProfile} from "@brg/auth"
import {useCallback, useState} from "react"
import {useToasts} from "@brg/ui"
import {useDispatch, useSelector} from "react-redux"
import {useModal} from "core"
import {useTranslation} from "react-i18next"
import {FlowType} from "../constants/flowType";
import {
    clearImmediatePurchaseProps,
    clearOrderFields,
    clearOrderFieldsWithoutStep,
    goToStep,
    selectorOrder,
    setActiveTariff,
} from "../slices/OrderSlice";
import economicUrlApi from "../helpers/economicUrlApi";
import {selectorSendGift} from "../slices/SendGiftSlice";
import {OrderStatuses} from "../payment/CheckOut/types";
import {runImmediatePurchase} from "../helpers/payment/runImmediatePurchase";
import {userCurrenciesState} from "../slices/userCurrenciesSlice";
import { CurrencyType } from "@brg/api"
import { PaymentCurrency, UserPaymentCurrency } from "../constants/types"
import { isInsufficientFunds } from "../payment/CheckOut/helpers/validators"
import { CurrencyAccountType, isCompositeCurrency, searchCurrencyItem } from "../payment/CheckOut/helpers/utils"
import { bookFlowSteps } from "../payment/Flows"

export interface UsePay {
    handleClick?: (params?: any) => void
    isGift?: boolean
    onPaymentSuccess?: (params?: any) => void
    onPaymentFail?: (params?: any) => void
    onNotEnoughMoney?: (params?: any) => void
    userBalance: userCurrenciesState
    competitionId?: string
    selectedProjectId?: string
}

export const usePay = (props: UsePay) => {
    const {
        handleClick,
        isGift,
        onNotEnoughMoney,
        onPaymentFail,
        onPaymentSuccess,
        userBalance,
        competitionId,
        selectedProjectId,
    } = props
    const { t } = useTranslation()
    const { info, error } = useToasts()
    const profile = useProfile()
    const dispatch = useDispatch()
    const modal = useModal()
    const Auth = useAuth()
    const order = useSelector(selectorOrder)
    const gift = useSelector(selectorSendGift)
    const [isOrderChecking, setIsOrderChecking] = useState(false)
    // const appName
    const paymentCurrencyInit = { currencyType: order.currencyType || CurrencyType.RealMoney, countryId: null, amount: order.price }
    const [paymentCurrency, setPaymentCurrency] = useState<PaymentCurrency>(paymentCurrencyInit);
    const isModer = profile?.rolesList?.includes('moder')
    const isGalleryModer = isModer || profile?.rolesList?.includes('gallerymoder') //zelandmoder

    const checkOrderStatus = useCallback(async (order: any) => {
        try {
            if (!profile) return
            if (!order?.orderId) {
                onPaymentFail && onPaymentFail({ order, err: 'orderId is empty' })
                return
            }
            setIsOrderChecking(true)
            //waiter
            const waitCount = 10
            const waitingTime = 3000
            let orderStatus: any
            for (let i = 0; i <= waitCount; i++) {
                orderStatus = await economicUrlApi().getOrder(order?.orderId)
                const paymentCurrencyLocal = order?.paymentCurrency

                if (orderStatus.orderStatusType === OrderStatuses.successAccepted) {
                    console.log('use pay payment success', onPaymentSuccess)
                    if (onPaymentSuccess) {
                        onPaymentSuccess?.(orderStatus)
                    } else {
                        info("Успешная оплата")
                    }
                    break
                } else if (orderStatus.orderStatusType === OrderStatuses.failedRejected) {
                    if (onPaymentFail) {
                        const skipCheckingFail = paymentCurrencyLocal?.RUB?.amount      //при наличии рублевой составляющей будет переход на платежный шлюз, а значит проверка не имеет смысла
                        !skipCheckingFail && onPaymentFail(orderStatus)
                    } else {
                        error(t('payment.errors.try_again_balance'))
                    }
                    break
                } else if (orderStatus.orderStatusType === OrderStatuses.awaitingPayment) {
                    await new Promise(resolve => setTimeout(resolve, waitingTime))
                } else if (orderStatus.orderStatusType === OrderStatuses.awaitingAcquiringPayment) {
                    const savedOrder = JSON.stringify(order)
                    const productId = order?.productId || order?.immediatePurchaseProps?.productId || (order?.products?.length && order?.products[0].productId)
                    
                    localStorage.setItem(`${order.flowType}_${productId}`, savedOrder || "");
                    const acquiringUrl = orderStatus?.acquiringUrl
                    window.location.href = acquiringUrl
                    break
                } else {
                    await new Promise(resolve => setTimeout(resolve, waitingTime))
                }
            }

            if (!(orderStatus && [OrderStatuses.successAccepted, OrderStatuses.awaitingPayment, OrderStatuses.awaitingAcquiringPayment].includes(orderStatus.orderStatusType)) ||
                (orderStatus.orderStatusType === OrderStatuses.failedRejected)
            ) {
                //переход на страницу ошибки платежа
                onPaymentFail && onPaymentFail({ order, orderStatus })
                return
            }
            const availableCurrencies = order?.availableCurrencies
            if (!isGift && ![FlowType.BUY_BOOK, FlowType.COPY_BOOK, FlowType.COPY_CARD].includes(order?.flowType)) dispatch(clearOrderFields())
            if (!isGift && ![FlowType.BUY_BOOK, FlowType.COPY_BOOK, FlowType.COPY_CARD].includes(order?.flowType)) dispatch(clearOrderFieldsWithoutStep())
        } catch (err: any) {
            console.error(err);
            error(err.message)
            onPaymentFail && onPaymentFail({ order, error: err })
        } finally {
            setIsOrderChecking(false)
        }

    }, [profile, onPaymentFail, onPaymentSuccess])

    const pay = useCallback(async () => {
        try {
            if (isOrderChecking) return
            if (!userBalance.currencies) return
            if (!profile || !Auth.profile || !Auth.token) {
                error(t('card_constructor.info_panel.error_not_login'))
                onPaymentFail && onPaymentFail()
                return
            }

            const userCurrentPaymentCurrency = searchCurrencyItem({
                accountType: CurrencyAccountType.user,
                currencies: userBalance.currencies,
                search: { 
                    countryId: isCompositeCurrency(paymentCurrency) ? undefined : paymentCurrency?.countryId, 
                    currencyType: CurrencyType.RealMoney 
                },
            }) as UserPaymentCurrency | undefined

            const actualPaymentCurrency = searchCurrencyItem({
                accountType: CurrencyAccountType.payment,
                currencies: order.paymentCurrencies,
                search: { 
                    countryId: isCompositeCurrency(paymentCurrency) ? undefined : paymentCurrency?.countryId, 
                    currencyType: order.currencyType
                },
            }) as PaymentCurrency | undefined

            
            const isLessMoney = isInsufficientFunds({ paymentCurrency, userBalance })

            if (isLessMoney && !isGalleryModer) {
                if (onNotEnoughMoney) {
                    onNotEnoughMoney()
                } else {
                    modal.hide()
                    error("Недостаточно средств")
                    dispatch(goToStep(bookFlowSteps.errorPayment))
                }
                return
            }
            
            let usedOrder = order

            const currentFlowType = order?.immediatePurchaseProps?.flowType || order?.flowType;
            const isImmediate = order?.immediatePurchaseProps && currentFlowType

            console.log("aaaa", isLessMoney, isImmediate, order, currentFlowType)

            if (isImmediate) {
                const { usedOrder: newUsedOrder } = await runImmediatePurchase({
                    dispatch,
                    isGalleryModer,
                    order,
                    error,
                    productPrice: paymentCurrency,
                    needAcquiring: true,
                    profile,
                    competitionId,
                    selectedProjectId,
                })
                //@ts-ignore
                usedOrder = newUsedOrder
            }

            await checkOrderStatus(usedOrder)
            dispatch(clearImmediatePurchaseProps(null))
            dispatch(setActiveTariff(null))
            handleClick?.(usedOrder)
        } catch (err: any) {
            console.error('pay err:', err, order);
            onPaymentFail && onPaymentFail()
            error(err.message)
        }
    }, [isOrderChecking, profile, Auth.profile, Auth.token, isGift, userBalance, paymentCurrency, order, isGalleryModer, checkOrderStatus, dispatch, handleClick, error, t, onPaymentFail, gift?.res?.id, onNotEnoughMoney, modal])

    return {
        isOrderChecking,
        pay,
        paymentCurrency,
        setPaymentCurrency,
    }
}import {useAuth, useProfile} from "@brg/auth"
import {useCallback, useState} from "react"
import {useToasts} from "@brg/ui"
import {useDispatch, useSelector} from "react-redux"
import {useModal} from "core"
import {useTranslation} from "react-i18next"
import {FlowType} from "../constants/flowType";
import {
    clearImmediatePurchaseProps,
    clearOrderFields,
    clearOrderFieldsWithoutStep,
    goToStep,
    selectorOrder,
    setActiveTariff,
} from "../slices/OrderSlice";
import economicUrlApi from "../helpers/economicUrlApi";
import {selectorSendGift} from "../slices/SendGiftSlice";
import {OrderStatuses} from "../payment/CheckOut/types";
import {runImmediatePurchase} from "../helpers/payment/runImmediatePurchase";
import {userCurrenciesState} from "../slices/userCurrenciesSlice";
import { CurrencyType } from "@brg/api"
import { PaymentCurrency, UserPaymentCurrency } from "../constants/types"
import { isInsufficientFunds } from "../payment/CheckOut/helpers/validators"
import { CurrencyAccountType, isCompositeCurrency, searchCurrencyItem } from "../payment/CheckOut/helpers/utils"
import { bookFlowSteps } from "../payment/Flows"

export interface UsePay {
    handleClick?: (params?: any) => void
    isGift?: boolean
    onPaymentSuccess?: (params?: any) => void
    onPaymentFail?: (params?: any) => void
    onNotEnoughMoney?: (params?: any) => void
    userBalance: userCurrenciesState
    competitionId?: string
    selectedProjectId?: string
}

export const usePay = (props: UsePay) => {
    const {
        handleClick,
        isGift,
        onNotEnoughMoney,
        onPaymentFail,
        onPaymentSuccess,
        userBalance,
        competitionId,
        selectedProjectId,
    } = props
    const { t } = useTranslation()
    const { info, error } = useToasts()
    const profile = useProfile()
    const dispatch = useDispatch()
    const modal = useModal()
    const Auth = useAuth()
    const order = useSelector(selectorOrder)
    const gift = useSelector(selectorSendGift)
    const [isOrderChecking, setIsOrderChecking] = useState(false)
    // const appName
    const paymentCurrencyInit = { currencyType: order.currencyType || CurrencyType.RealMoney, countryId: null, amount: order.price }
    const [paymentCurrency, setPaymentCurrency] = useState<PaymentCurrency>(paymentCurrencyInit);
    const isModer = profile?.rolesList?.includes('moder')
    const isGalleryModer = isModer || profile?.rolesList?.includes('gallerymoder') //zelandmoder

    const checkOrderStatus = useCallback(async (order: any) => {
        try {
            if (!profile) return
            if (!order?.orderId) {
                onPaymentFail && onPaymentFail({ order, err: 'orderId is empty' })
                return
            }
            setIsOrderChecking(true)
            //waiter
            const waitCount = 10
            const waitingTime = 3000
            let orderStatus: any
            for (let i = 0; i <= waitCount; i++) {
                orderStatus = await economicUrlApi().getOrder(order?.orderId)
                const paymentCurrencyLocal = order?.paymentCurrency

                if (orderStatus.orderStatusType === OrderStatuses.successAccepted) {
                    console.log('use pay payment success', onPaymentSuccess)
                    if (onPaymentSuccess) {
                        onPaymentSuccess?.(orderStatus)
                    } else {
                        info("Успешная оплата")
                    }
                    break
                } else if (orderStatus.orderStatusType === OrderStatuses.failedRejected) {
                    if (onPaymentFail) {
                        const skipCheckingFail = paymentCurrencyLocal?.RUB?.amount      //при наличии рублевой составляющей будет переход на платежный шлюз, а значит проверка не имеет смысла
                        !skipCheckingFail && onPaymentFail(orderStatus)
                    } else {
                        error(t('payment.errors.try_again_balance'))
                    }
                    break
                } else if (orderStatus.orderStatusType === OrderStatuses.awaitingPayment) {
                    await new Promise(resolve => setTimeout(resolve, waitingTime))
                } else if (orderStatus.orderStatusType === OrderStatuses.awaitingAcquiringPayment) {
                    const savedOrder = JSON.stringify(order)
                    const productId = order?.productId || order?.immediatePurchaseProps?.productId || (order?.products?.length && order?.products[0].productId)
                    
                    localStorage.setItem(`${order.flowType}_${productId}`, savedOrder || "");
                    const acquiringUrl = orderStatus?.acquiringUrl
                    window.location.href = acquiringUrl
                    break
                } else {
                    await new Promise(resolve => setTimeout(resolve, waitingTime))
                }
            }

            if (!(orderStatus && [OrderStatuses.successAccepted, OrderStatuses.awaitingPayment, OrderStatuses.awaitingAcquiringPayment].includes(orderStatus.orderStatusType)) ||
                (orderStatus.orderStatusType === OrderStatuses.failedRejected)
            ) {
                //переход на страницу ошибки платежа
                onPaymentFail && onPaymentFail({ order, orderStatus })
                return
            }
            const availableCurrencies = order?.availableCurrencies
            if (!isGift && ![FlowType.BUY_BOOK, FlowType.COPY_BOOK, FlowType.COPY_CARD].includes(order?.flowType)) dispatch(clearOrderFields())
            if (!isGift && ![FlowType.BUY_BOOK, FlowType.COPY_BOOK, FlowType.COPY_CARD].includes(order?.flowType)) dispatch(clearOrderFieldsWithoutStep())
        } catch (err: any) {
            console.error(err);
            error(err.message)
            onPaymentFail && onPaymentFail({ order, error: err })
        } finally {
            setIsOrderChecking(false)
        }

    }, [profile, onPaymentFail, onPaymentSuccess])

    const pay = useCallback(async () => {
        try {
            if (isOrderChecking) return
            if (!userBalance.currencies) return
            if (!profile || !Auth.profile || !Auth.token) {
                error(t('card_constructor.info_panel.error_not_login'))
                onPaymentFail && onPaymentFail()
                return
            }

            const userCurrentPaymentCurrency = searchCurrencyItem({
                accountType: CurrencyAccountType.user,
                currencies: userBalance.currencies,
                search: { 
                    countryId: isCompositeCurrency(paymentCurrency) ? undefined : paymentCurrency?.countryId, 
                    currencyType: CurrencyType.RealMoney 
                },
            }) as UserPaymentCurrency | undefined

            const actualPaymentCurrency = searchCurrencyItem({
                accountType: CurrencyAccountType.payment,
                currencies: order.paymentCurrencies,
                search: { 
                    countryId: isCompositeCurrency(paymentCurrency) ? undefined : paymentCurrency?.countryId, 
                    currencyType: order.currencyType
                },
            }) as PaymentCurrency | undefined

            
            const isLessMoney = isInsufficientFunds({ paymentCurrency, userBalance })

            if (isLessMoney && !isGalleryModer) {
                if (onNotEnoughMoney) {
                    onNotEnoughMoney()
                } else {
                    modal.hide()
                    error("Недостаточно средств")
                    dispatch(goToStep(bookFlowSteps.errorPayment))
                }
                return
            }
            
            let usedOrder = order

            const currentFlowType = order?.immediatePurchaseProps?.flowType || order?.flowType;
            const isImmediate = order?.immediatePurchaseProps && currentFlowType

            console.log("aaaa", isLessMoney, isImmediate, order, currentFlowType)

            if (isImmediate) {
                const { usedOrder: newUsedOrder } = await runImmediatePurchase({
                    dispatch,
                    isGalleryModer,
                    order,
                    error,
                    productPrice: paymentCurrency,
                    needAcquiring: true,
                    profile,
                    competitionId,
                    selectedProjectId,
                })
                //@ts-ignore
                usedOrder = newUsedOrder
            }

            await checkOrderStatus(usedOrder)
            dispatch(clearImmediatePurchaseProps(null))
            dispatch(setActiveTariff(null))
            handleClick?.(usedOrder)
        } catch (err: any) {
            console.error('pay err:', err, order);
            onPaymentFail && onPaymentFail()
            error(err.message)
        }
    }, [isOrderChecking, profile, Auth.profile, Auth.token, isGift, userBalance, paymentCurrency, order, isGalleryModer, checkOrderStatus, dispatch, handleClick, error, t, onPaymentFail, gift?.res?.id, onNotEnoughMoney, modal])

    return {
        isOrderChecking,
        pay,
        paymentCurrency,
        setPaymentCurrency,
    }
}